import { Component } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { ActionSheetController, LoadingController, ToastController } from '@ionic/angular/lazy';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthError, AuthProvider } from 'src/app/services/auth.providers';
import { Router } from '@angular/router';
import { AppUpdateService } from 'src/app/services/app-update.service';
import { ApplicationEnvironment } from 'src/environments/application-environment.enum';
import { LoggingService } from '@app/core/logging/logging.service';
import { LoginResult, nameof, SmartMovingValidators, StronglyTypedFormBuilderService } from '@smartmoving/sm-shared-lib';
import { ENV } from '@app/app.config';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { SyncService } from '@app/core/sync-service';
import { getDeviceInfo } from '@pages/login/device-info.interface';
import { OfflineApiClient } from '@services/offline-api-client';
import { BiometricService } from '@services/biometric.service';
import { Credentials } from '@capgo/capacitor-native-biometric';
import { getBioMetricTypeText } from '@app/core/utils';
import { ThirdPartyIntegrationsService } from '@services/third-party-integration.service';
import { LoginForm } from '@generated/Authentication/login-form';
import { SmApplications } from '@generated/SmartMoving/Core/Data/Opportunities/sm-applications';
import { AppConfigService } from '@services/app-config.service';
import { environment } from 'src/environments/environment';

@UntilDestroy()
@Component({
  selector: 'sm-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false
})
export class LoginComponent {
  loginForm: FormGroup;
  userHasReachedMaxFailedLogins = false;
  isSubmitting = false;
  version = ENV.version;
  checkForUpdateCount = 0;
  changeEnvironmentCount = 0;
  isLoading = true;
  biometricLoginAvailable = false;
  biometricTypeText: string;
  buildNumber = ENV.buildNumber;
  isProd = environment.applicationEnvironment === ApplicationEnvironment.Production;
  private rememberEmailKey = 'SmartMoving.LoginEmailAddress';

  constructor(private readonly formBuilder: StronglyTypedFormBuilderService,
              private readonly auth: AuthProvider,
              private readonly toastController: ToastController,
              private readonly router: Router,
              public readonly loadingController: LoadingController,
              private readonly updater: AppUpdateService,
              private readonly actionSheetController: ActionSheetController,
              private readonly offlineApiClient: OfflineApiClient,
              private readonly syncService: SyncService,
              private readonly biometricService: BiometricService,
              private readonly thirdPartyService: ThirdPartyIntegrationsService,
              private readonly appConfigService: AppConfigService,
              private readonly loggingService: LoggingService) {
  }

  get loginFormValue() {
    return this.loginForm.value as LoginForm;
  }

  async loadData() {
    // Added just so that ion-toast module is initialized in web and can see the red toast if logging in while offline
    // noinspection ES6MissingAwait
    this.toastController.create();

    const fingerprint = await this.getUserFingerprint();

    this.loginForm = this.formBuilder.group<LoginForm>()
        .for(x => x.emailAddress, [null, [Validators.required, SmartMovingValidators.requiredEmail]])
        .for(x => x.password, [null, Validators.required])
        .for(x => x.rememberMe, [true])
        .for(x => x.appVersion, [this.version])
        .for(x => x.reCaptchaToken, [null])
        .for(x => x.fingerprint, [fingerprint])
        .for(x => x.application, [SmApplications.PreMoveSurveyApp])
        .for(x => x.iOSVersion, [null])
        .for(x => x.androidVersion, [null])
        .for(x => x.deviceInformation, [null])
        .for(x => x.isOfficeApplication, [false])
        .for(x => x.additionalLogs, [null])
        .build();

    const emailAddress = localStorage.getItem(this.rememberEmailKey);
    if (emailAddress) {
      this.loginForm.patchValue({
        emailAddress,
        rememberMe: true,
      });
    }
    this.loginForm.updateValueAndValidity();

    // Log when the network status changes while submitting login
    this.offlineApiClient.networkStatusChanged.pipe(untilDestroyed(this)).subscribe((isOnline) => {
      if (this.isSubmitting) {
        this.loggingService.info(`Network status changed while submitting login: connected = ${isOnline}`);
      }
    });

    this.isLoading = false;

    const credentials = await this.biometricService.getCredentials();

    if (credentials) {
      this.biometricTypeText = getBioMetricTypeText((await this.biometricService.isBiometricAvailable()).biometryType);
      this.biometricLoginAvailable = true;
      // Prompt with touch ID/face ID if this is the first page after app launch
      if (history.state.navigationId === 1) {
        await this.loginWithBiometric();
      }
    }
  }

  async ionViewDidEnter() {
    // Since ionic caches the component and doesn't rerender entire component again, initialization must be done on Ionic life cycle hooks.
    await this.loadData();
  }

  async checkForUpdate() {
    if (++this.checkForUpdateCount !== 10) {
      return;
    }
    this.checkForUpdateCount = 0;

    const loader = await this.loadingController.create({
      message: 'Checking for updates, please wait...'
    });
    await loader.present();

    let failure: unknown;
    try {
      if (!await this.updater.needsUpdate()) {
        await (await this.toastController.create({
          message: 'No updates available',
          duration: 3000
        })).present();
      } else {
        loader.message = 'Downloading update, please wait...';
        await this.updater.updateNow({ showErrors: true });
      }
    } catch (err) {
      failure = err;
    } finally {
      await loader.dismiss();
    }

    if (failure) {
      await this.updater.showError(failure);
    }
  }

  async login(credentials: Credentials = null) {
    if (!credentials && this.loginForm.invalid || this.isSubmitting) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const config = await this.appConfigService.getConfig();
    const deviceInfo = await getDeviceInfo();
    this.loginForm.get(nameof<LoginForm>(x => x.iOSVersion)).setValue(deviceInfo.platform === 'ios' ? deviceInfo.version : '');
    this.loginForm.get(nameof<LoginForm>(x => x.androidVersion)).setValue(deviceInfo.platform === 'android' ? deviceInfo.version : '');
    this.loginForm.get(nameof<LoginForm>(x => x.deviceInformation)).setValue(JSON.stringify(deviceInfo));
    this.loginForm.get(nameof<LoginForm>(x => x.additionalLogs)).setValue(config.additionalLoginInfo);

    await this.attemptLogin(credentials);
  }

  async loginWithBiometric() {
    if (!(await this.biometricService.verifyIdentity('Log in', 'Verify to log in'))) {
      return;
    }
    const credentials = await this.biometricService.getCredentials();
    if (credentials) {
      await this.login(credentials);
    }
  }

  async ionViewDidLeave() {
    try {
      await this.toastController.dismiss();
    } catch {
      // Toast already closed
    }
  }

  async changeEnvironment() {
    if (++this.changeEnvironmentCount !== 10) {
      return;
    }

    this.changeEnvironmentCount = 0;

    const actionSheet = await this.actionSheetController.create({
      header: 'Choose Environment',
      backdropDismiss: false,
      buttons: [{
        text: 'Production',
        icon: 'pulse-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.Production);
        }
      }, {
        text: 'Development',
        icon: 'code-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.Dev);
        }
      }, {
        text: 'QA-1',
        icon: 'bug-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.QA1);
        }
      }, {
        text: 'QA-2',
        icon: 'bug-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.QA2);
        }
      }, {
        text: 'QA-3',
        icon: 'bug-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.QA3);
        }
      }, {
        text: 'QA-4',
        icon: 'bug-outline',
        handler: async () => {
          await this.doEnvironmentChange(ApplicationEnvironment.QA4);
        }
      }, {
        text: 'Cancel',
        icon: 'close',
        role: 'cancel',
        handler: () => {
          // Nothing to do!
        }
      }]
    });
    await actionSheet.present();
  }

  private async getUserFingerprint() {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();

      return result.visitorId;
    } catch {
      // Fire the global error handler so we know what went wrong...

      // eslint-disable-next-line no-warning-comments
      // TODO: add global error handling
      // this.errorHandler.handleError(err).then(() => {
      // });
      // But return something so that the user isn't blocked from logging in.
      return 'unknown';
    }
  }

  private async attemptLogin(credentials: Credentials) {
    this.isSubmitting = true;

    let retryCount = 0;
    const maxRetries = 3;
    const retryDelay = 2000; // 2 seconds to give the connection time to stabilize

    /*
      The sm-shared-lib api service will retry five times.
      If those all fail, we consider that a failed "unit of work" and try again up to a total of maxRetries.
      So, total attempts = maxRetries * 5.
     */
    this.loggingService.info('Attempting login...');
    while (retryCount <= maxRetries) {
      try {
        let user;
        if (!credentials) {
          user = await this.auth.login(this.loginFormValue);
          await this.biometricService.promptModalIfFirstLoginAndStoreCredentials(
              this.loginFormValue.emailAddress,
              this.loginFormValue.password
          );
        } else {
          user = await this.auth.login({
            ...this.loginFormValue,
            emailAddress: credentials.username,
            password: credentials.password
          });
        }

        this.thirdPartyService.init(user);
        await this.appConfigService.clearAdditionalLoginInfo();
        await this.handleSuccessfulLogin(user);
        this.isSubmitting = false;
        return; // Success
      } catch (error) {
        // If network error, attempt batched retries
        if (error === AuthError.NetworkError) {
          retryCount++;
          if (retryCount <= maxRetries) {
            // Show a toast to inform the user we're retrying
            const message = `Connection issue detected. Retrying login... (${retryCount} of ${maxRetries})`;
            const toast = await this.toastController.create({
              message,
              duration: retryDelay - (retryDelay / 4), // Show for 3/4 of the retry delay
              position: 'bottom'
            });
            await toast.present();
            this.loggingService.info(message);
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }
        }

        // Handle other errors normally
        this.isSubmitting = false;

        // If we've reached/surpassed the amount of retries, then there is likely a network issue.
        const networkFailure = retryCount >= maxRetries;

        await this.handleFailedLogin(error, !!credentials, networkFailure);
        return;
      }
    }
  }

  private async handleFailedLogin(error, usedBiometric = false, networkFailure = false) {
    this.updateMaxFailedLogins(error.error?.userHasReachedMaxFailedLogins);

    this.loginForm.get('reCaptchaToken').setValue(null);

    let toastMessage = 'Incorrect Credentials';

    // If this is a network failure, then no auth happened, so there's nothing to check.
    // Inform the user, then return
    if (networkFailure) {
      toastMessage = 'We are unable to log you in due to network connectivity issues. Please check your internet connection or try again later.';
      this.loggingService.info('Login retries failed. Network experiencing connectivity issues.');
      await this.showToast(toastMessage);
      return;
    }

    // Do rest of failed auth checkss
    if (usedBiometric) {
      toastMessage = `Please log in with password once to re-enable ${this.biometricTypeText} authentication.`;
      // Remove the stored invalid credentials, so that user don't see this option until logs in with password.
      await this.biometricService.deleteCredentials();
      this.biometricLoginAvailable = false;
    } else if (this.userHasReachedMaxFailedLogins) {
      toastMessage = 'You have exceeded the amount of failed login attempts. Please enter your password again and click "I\'m not a robot".';
    }

    await this.showToast(toastMessage);
  }

  private async showToast(toastMessage: string) {
    const toastCtrl = await this.toastController.create({
      message: toastMessage,
      duration: 4000,
      buttons: ['OK']
    });
    await toastCtrl.present();
  }

  private updateMaxFailedLogins(userHasReachMaxFailedLogins: boolean) {
    this.userHasReachedMaxFailedLogins = userHasReachMaxFailedLogins;

    if (userHasReachMaxFailedLogins) {
      this.loginForm.get(nameof<LoginForm>(x => x.reCaptchaToken)).setValidators([Validators.required]);
    } else {
      this.loginForm.get(nameof<LoginForm>(x => x.reCaptchaToken)).setValidators([]);
    }
  }

  private async doEnvironmentChange(env: ApplicationEnvironment) {
    const loader = await this.loadingController.create({
      message: 'Changing environments, please wait...'
    });
    await loader.present();

    let failure: unknown;
    try {
      await this.updater.changeEnvironment(env);
    } catch (err) {
      failure = err;
    } finally {
      await loader.dismiss();
    }

    if (failure) {
      await this.updater.showError(failure);
    }
  }

  private async handleSuccessfulLogin(user: LoginResult) {
    if (user.isLockedOutDueToPayment) {
      await this.router.navigate(['/locked-out']);
      return;
    }

    this.loggingService.info('Login successful!');

    if (this.loginFormValue.rememberMe) {
      localStorage.setItem(this.rememberEmailKey, this.loginFormValue.emailAddress);
    } else {
      localStorage.removeItem(this.rememberEmailKey);
    }

    this.updateMaxFailedLogins(false);

    this.isLoading = true;
    await this.syncService.fullResync(true);
    this.loginForm.reset();

    await this.router.navigate(['/schedule'], { replaceUrl: true });
  }
}
